/**
 * WARNING. DANGER.
 * STOP!
 * THIS FILE IS SENSITIVE.
 * Do not edit this file without plan
 * for dual routers regression testing
 */

import Constants, { EventConsts, LoginConsts, UnsavedChangesModalConsts } from 'client/util/Constants';
import Cookies from 'js-cookie';
import LoggingService from 'client/util/LoggingService';
import ModalEventBus, { ModalEvents } from 'client/components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import globalRouteService from 'client/AdminPortal/router/globalRouteService';

export const unsavedChangesMessage = 'Any unsaved changes you have made will be lost. Would you like to continue?';

const { clientTypeCookie } = LoginConsts;
const { admin } = LoginConsts.clientType;

/**
 * The PageNavigationUtil object is used to add statefulness to site so that
 * when an admin user closes an aside panel or leaves the site with out saving
 * their changes they will be prompted to save them before leaving.
 *
 * When Vue Router is used instead of the Backbone Router most of the
 * functionality here can be replaced with Vue Router's navigation guards.
 * Backbone does not have similar functionality.
 * @see https://router.vuejs.org/guide/advanced/navigation-guards.html
 */
export function createPageNavigationUtil(
  _globalRouteService = globalRouteService,
  _modalEventBus = ModalEventBus,
  _mspState = MspState,
  _cookies = Cookies,
) {
  return {
    eventListener: null,
    oldBackboneHistoryLoadUrl: null,
    router: null,
    storageEventListener: null,

    // The following methods wrap methods and properties on the global window object so they can
    // more easily be stubbed in unit tests.

    addEventListener(event, method) {
      window.addEventListener(event, method);
    },

    removeEventListener(event, method) {
      window.removeEventListener(event, method);
    },

    getLocationHash() {
      return window.location.hash;
    },

    getLocationHashPath() {
      return window.location.hash.replace('#', '');
    },

    getSearchParams() {
      const paramObj = {};
      const params = new URLSearchParams(this.getLocationHash().split('?')[1] || '');
      params.forEach((value, key) => {
        paramObj[key] = value;
      });
      return paramObj;
    },

    setLocationHash(hash) {
      window.location.hash = hash;
    },

    setLocation(url) {
      window.location.href = url;
    },

    setLocalStorageItem(key, value) {
      window.localStorage.setItem(key, value);
    },

    setSessionStorageItem(key, value) {
      window.sessionStorage.setItem(key, value);
    },

    removeLocalStorageItem(key) {
      window.localStorage.removeItem(key);
    },

    get backboneRouter() {
      return this.backboneApp && this.backboneApp.router;
    },

    /**
     * Set up event handlers and method overrides to warn the admin user when
     * leaving an aside panel with out saving changes.
     */
    addEventHandlers({ backboneApp = null, vueApp = null }) {
      this.backboneApp = backboneApp;
      this.vueApp = vueApp;

      this.eventListener = this.handleBeforeUnload.bind(this);
      this.addEventListener(EventConsts.beforeunload, this.eventListener);

      this.storageEventListener = this.handleLogoutEvent.bind(this);
      this.addEventListener(EventConsts.storage, this.storageEventListener);

      this.oldBackboneHistoryLoadUrl = Backbone.history.loadUrl;
      Backbone.history.loadUrl = this.handleLoadUrl.bind(this);
    },

    /**
     * Remove the event handlers and overridden methods that were added to warn the
     * admin user when leaving an aside panel with out saving changes. This method
     * is added to the global window object so that it can be accessed from both
     * the jQuery ajax and axios 401 handlers.
     */
    removeEventHandlers() {
      this.removeEventListener(EventConsts.beforeunload, this.eventListener);
      this.removeEventListener(EventConsts.storage, this.storageEventListener);
      Backbone.history.loadUrl = this.oldBackboneHistoryLoadUrl;
    },

    /**
     * Warn the user when leaving the site if they have made changes in an Aside
     * Panel and are leaving with out saving them.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
     */
    handleBeforeUnload(e) {
      if (this.hasModelChanged()) {
        e.preventDefault();
        e.returnValue = unsavedChangesMessage;
        return unsavedChangesMessage;
      }

      return undefined;
    },

    /**
     * Override loadUrl method from Backbone.history to ask the router if it can
     * navigate to a new page. This method will query the Aside Panel, if open,
     * and warn the user if they are navigating to a new page before saving their
     * changes.
     * @see https://stackoverflow.com/questions/9872988/prevent-navigating-to-another-view-if-contents-are-unsaved
     */
    handleLoadUrl(...args) {
      LoggingService.debug('PageNavigationUtil.handleLoadUrl: ', ...args);
      LoggingService.debug('PageNavigationUtil.handleLoadUrl isVueRoute',
        _globalRouteService.isVueRoute());
      const openModal = ({ headerText, bodyText }) => {
        const componentProps = {
          headerText,
          bodyText,
          handleCancel: () => this.handleLoadUrlCancel(),
          handleContinue: () => this.handleLoadUrlContinue(args),
          showClose: false,
        };

        _modalEventBus.open({
          isLargeModal: false,
          component: 'ConfirmationModal',
          componentProps,
          preventCloseOnOverlayClick: true,
        });
      };

      // If Vue Router has control just return without doing anything
      if (_globalRouteService.isVueHandlingRoute()) {
        LoggingService.debug('PageNavigationUtil.handleLoadUrl Vue is handling Route');
        return false;
      }

      if (this.isModelProcessing()) {
        LoggingService.debug('PageNavigationUtil.handleLoadUrl Model is processing: ',
          _globalRouteService.isVueRoute());

        const processingData = this.backboneRouter.getModelProcessingData();
        if (processingData) {
          openModal(processingData.modalProps);
          _globalRouteService.resolveRoute(false);
          return false;
        }
      }

      if (this.hasModelChanged()) {
        LoggingService.debug('PageNavigationUtil.handleLoadUrl: Model has changed');
        openModal(UnsavedChangesModalConsts);
        _globalRouteService.resolveRoute(false);
        return false;
      }

      // If we're navigating to a Vue route pass control back to Vue Router
      if (_globalRouteService.isVueRoute()) {
        LoggingService.debug('PageNavigationUtil.handleLoadUrl is Vue Route');
        this.passToVueRouter(...args);
        _globalRouteService.resolveRoute(false);
        return false;
      }

      _globalRouteService.resolveRoute(true);
      return this.loadBackboneUrl(...args);
    },

    loadBackboneUrl(...args) {
      _globalRouteService.updateRoute();
      this.oldBackboneHistoryLoadUrl.apply(Backbone.history, args);
    },

    handleLoadUrlCancel() {
      _modalEventBus.close();
      const previousFragment = Backbone.history.fragment;
      this.setLocationHash(`#${previousFragment}`);
    },

    /**
     * Wait until TheModal has transitioned out before navigating to the new
     * page. This is done so that the Aside Panel close animation occurs _after_
     * TheModal close animation.
     */
    handleLoadUrlContinue(args) {
      _modalEventBus.$once(ModalEvents.transitionEnd, () => {
        if (_globalRouteService.isVueRoute()) {
          this.passToVueRouter();
        } else {
          this.oldBackboneHistoryLoadUrl.apply(Backbone.history, args);
        }
      });
      _modalEventBus.close();
    },

    /**
     * When the storage event is triggered check if the logoutEvent key's value
     * was updated, and if so then log the user out in the current tab.
     */
    handleLogoutEvent(e) {
      if (e.key === EventConsts.logoutEvent) {
        this.setLocation('#/logout');
      }
    },

    /**
     * If a 401 error is received, clear the sessionStorage variables used to track the MSP
     * state before returning to the login page. This will prevent other errors on login
     * caused by having an incorrect MSP state.
     */
    handle401() {
      this.removeEventHandlers();
      this.saveRequestedPage();
      _mspState.clearStorage();
      const type = _cookies.get(clientTypeCookie);
      if (type === admin) {
        this.setLocation('/logout');
      } else {
        this.setLocation('/userconsole/logout');
      }
    },

    passToVueRouter() {
      _globalRouteService.passToVue();
      _globalRouteService.triggerVueNavigation();
    },

    /**
     * Query the Backbone Router and/or the Vue Router to see if the model has changed. This method
     * is called by the global beforeunload event handler and the home grown navigation guards used
     * in the Backbone application.
     */
    hasModelChanged() {
      const { asidePanel = null, mainPanel = null } = this.backboneRouter || {};
      let hasModelChanged = false;

      if ((asidePanel && asidePanel.hasModelChanged && asidePanel.hasModelChanged())
        || (mainPanel && mainPanel.hasModelChanged && mainPanel.hasModelChanged())) {
        hasModelChanged = true;
      }

      return hasModelChanged;
    },

    isModelProcessing() {
      return !!(this.backboneRouter && this.backboneRouter.isModelProcessing());
    },

    /**
     * Save the requested "page" so that after logging in the admin user can
     * be redirected to the page they requested (currently only used for Workday Jobs).
     * @todo If/when we use real URLs this will need to change to reference
     * the pathname property of the location.
     */
    saveRequestedPage() {
      const hash = this.getLocationHash();

      if (hash && !hash.match('logout')) {
        this.setSessionStorageItem(Constants.redirectTo, `/${hash}`);
      }
    },
  };
} // end createPageNavigationUtil()

export default createPageNavigationUtil();
